"use strict";

const $ = require("jquery");
const cssModule = require("js/css_constants");
const utilModule = require("js/util");
const cards = require("js/cards");
const eventStream = require("js/eventstream");
const events = require("js/event_helper");
const sc = require("js/utils/state_capture");
const tracker = require("js/utils/focus_tracker.js");
const keyModule = require("js/keyboard.js");
const cardViewUtils = require("js/editor/card_view_utils.js");

import { FILTER_REQUEST_SEARCH, FILTER_SAVE_NEW } from "js/search/filter-default.js";

export const SEARCH_HORIZON = "card-search-search-horizon";

const TRIGGER_NAVIGATION = "card-search-trigger-navigation";

// this is sort of a hack that we need to keep around as long as the layout_component.js is
// still written in JavaScript and has not been migrated to the LiveView. Basically just function
// stubs that I copied from the original version of this module (when it was still all JS), plus
// the activate and deactivate functions to actually work (they set/remove the disabled class)
export const init = function (selector, params = {}) {
    const component = document.querySelector(selector);
    component.lastState = __createMemento();

    function __isActive() {
        return utilModule.isActive(selector);
    }

    function __getState() {
        // we need to return a pseudo state (that we can actually use in the future), so that
        // we can create a nice memento on the server that looks exactly the same like the one
        // created in the JS code
        return sc.componentState(
            this,
            () => {
                const filter = __getFilter();

                return {
                    horizon: component.dataset.currentHorizonId,
                    collapsed: utilModule.parseBool(component.dataset.horizonsCollapsed),
                    tab: component.dataset.currentTabId,
                    sort: component.querySelector("ldc-card-search-result").sortBy,
                };
            },
            () => {
                component.lastState = __createMemento();
                return component.lastState;
            },
        );
    }

    function __createMemento() {
        const filter = __getFilter();
        return {
            horizon: component.dataset.currentHorizonId,
            collapsed: utilModule.parseBool(component.dataset.horizonsCollapsed),
            tab: component.dataset.currentTabId,
            filter: filter.createMemento(),
            sort: component.querySelector("ldc-card-search-result").sortBy,
        };
    }

    // So for #937 I added this fix (commit 592cef8f), but then that caused bug #1026.
    // I reverted to the function which ws before here (check commit diff to see what was there)
    function activate(memento) {
        console.log("CardSearch: (" + selector + "): ACTIVATE");

        if (memento != null && memento.memento != null) {
            if (memento.memento.filter != null) {
                __getFilter().loadMemento(memento.memento.filter);
            }

            if (memento.memento.sort != null) {
                component.querySelector("ldc-card-search-result").sortBy = memento.memento.sort;
            }

            // use event to trigger navigation, cause we do not have access to the
            // LiveView instance here
            events.dispatch(TRIGGER_NAVIGATION, component, memento.memento);
        }
    }

    function __getFilter() {
        return component.querySelector("ldc-filter-default");
    }

    return {
        activate: utilModule.Activator(selector, activate),
        deactivate: utilModule.Deactivator(selector, async () => {
            console.log("CardSearch: (" + selector + ": DEACTIVATE");

            /* Activate the loading overlay, because once teh search is activated again
             * a new search result is always requested. However because there are different
             * code paths for activating the saerch again, with different timings for the
             * arrival of the search results and the reactivation of the search, we must
             * enable the overaly in the deactivate function instead of the activate
             * function */
            component.querySelector("ldc-card-search-result").showLoadingOverlay();
        }),
        isActive: __isActive,
        getState: __getState,
        scrollToTop: () => {
            component.scrollTop = 0;
            component.scrollIntoView(true);
        },
    };
};

export const hooks = {
    CardSearchHook: {
        mounted() {
            const self = this;
            const container = this.el;

            const filter = container.querySelector("ldc-filter-default");
            const phxTarget = container.getAttribute("phx-target");

            __getSearchResult().initChannel();

            function __getSearchResult() {
                return container.querySelector("ldc-card-search-result");
            }

            function showLoadingOverlay() {
                const searchResults = __getSearchResult();

                // will be null on first run, cause no search results have been fetched from the server yet
                if (searchResults != null) {
                    searchResults.showLoadingOverlay();
                }
            }

            function sortBtnClicked(target, name) {
                if (target.matches("th." + name) || target.closest("th." + name) != null) {
                    showLoadingOverlay();
                    self.pushEventTo(phxTarget, "sort-by", { field: name });
                }
            }

            console.log(`Card Search with id "${container.id}" mounted`);

            container.addEventListener("click", (e) => {
                sortBtnClicked(e.target, "title");
                sortBtnClicked(e.target, "marker");
                sortBtnClicked(e.target, "last_user_action");
                sortBtnClicked(e.target, "original_owner");
                sortBtnClicked(e.target, "shared_with");
            });

            container.addEventListener(TRIGGER_NAVIGATION, (e) => {
                self.pushEventTo(phxTarget, "browser-navigation", e.detail);
            });

            container.addEventListener("tab-pane-tab-clicked", showLoadingOverlay);

            container.addEventListener(FILTER_REQUEST_SEARCH, (e) => {
                this.pushEventTo(phxTarget, "search-current-filter-tab", e.detail);
                showLoadingOverlay();
            });

            console.log("Register container: ", container);
            container.addEventListener(FILTER_SAVE_NEW, (e) => {
                console.log("trigger persist-new-filter event", e.detail);
                this.pushEventTo(phxTarget, "persist-new-filter", e.detail);
            });

            container.addEventListener(SEARCH_HORIZON, (e) => {
                const detail = e.detail;
                detail["force-enable"] = true;
                this.pushEventTo(phxTarget, "search-in-horizon", detail);
                showLoadingOverlay();
            });

            document.addEventListener("keydown", triggerShortcut);

            function triggerShortcut(event) {
                if (utilModule.isActive(container) && tracker.hasLastFocus(container)) {
                    if (keyModule.ctrlMeta(event) && event.which == keyModule.keys.key_a) {
                        event.preventDefault();
                        event.stopPropagation();
                        __getSearchResult().selectAll();
                        console.log("CTRL-A pressed: select all");
                    }
                }
            }

            // store the callback function in the view so that it is availble in the updated() hook
            container.fixLiveEnabledStatus = utilModule.fixLiveEnabledStatus(container, [
                ".component-layout",
                "#modal-popup-container",
            ]);

            function pushEnabledStatus(val) {
                // include the search terms for the current filter settings. Necessary for forward/backward
                // browser navigation
                const request = filter.getSearchRequest();
                request.enabled = val;
                self.pushEventTo(phxTarget, "set-enabled-status", request);
            }

            // notify LiveComponent about enabled status, which is managed by JS code
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    const isDisabled = container.classList.contains(cssModule.disabled);
                    const wasDisabled = mutation.oldValue.split(" ").find((val) => val == cssModule.disabled) != null;

                    if (isDisabled != wasDisabled) {
                        pushEnabledStatus(!isDisabled);
                    }
                });
            });

            observer.observe(container, { attributes: true, attributeFilter: ["class"], attributeOldValue: true });

            // init value
            pushEnabledStatus(!container.classList.contains(cssModule.disabled));
            console.log("mount done");

            // For the tutorial we need to send messages to the backend at some independent location.
            // I could not find the correct way to get the live-view object from elsewhere, this was the easiest
            // way I could find.
            // TODO: Check with clemens.
            if (self.el.id == "left-component-card-search") {
                if (!window.ldc.tutorial) {
                    window.ldc.tutorial = {};
                }
                window.ldc.tutorial.leftSearchComponentLiveView = this;
            }
        },

        updated() {
            const container = this.el;
            const filter = container.querySelector("ldc-filter-default");
            const phxTarget = container.getAttribute("phx-target");

            if (container.dataset.currentHorizonChanged == "true") {
                filter.updateCurrentHorizon(container.dataset.currentHorizonId);
            }

            container.fixLiveEnabledStatus(() => {
                console.log("Called fix live enabled status");
                filter.reset();
            });

            console.log("Updated called");
        },
    },
};
